// Shrink a picked image before upload.
//
// A coach picks whatever their phone took — a 4MB photo of a logo on a screen —
// and the API caps uploads at 512KB. Rather than reject that and make them find
// an image editor, resize it here: the result is a logo displayed at ~40px, so
// nothing above a few hundred pixels is doing any work.
//
// Also normalizes format. A HEIC or GIF that the browser can decode comes out
// as PNG or JPEG, which is what the API accepts.

export interface ResizedImage {
  /** Base64 WITHOUT the data: prefix — what the API wants. */
  dataBase64: string;
  contentType: "image/png" | "image/jpeg";
  /** Full data URL, for previewing before upload. */
  previewUrl: string;
  bytes: number;
}

export interface ResizeOptions {
  /** Longest edge, in pixels. */
  maxDimension?: number;
  /** Give up above this, rather than produce something the API will reject. */
  maxBytes?: number;
}

/**
 * Resize to fit `maxDimension`, preserving aspect ratio and never upscaling.
 *
 * Transparency decides the output format: PNG keeps it (a logo on a coloured
 * header would otherwise gain a white box), JPEG is used when there's none
 * because it's several times smaller for a photograph.
 *
 * Quality steps down only if the first attempt is too big, so a small image
 * isn't degraded for no reason.
 */
export async function resizeImage(file: File, options: ResizeOptions = {}): Promise<ResizedImage> {
  const maxDimension = options.maxDimension ?? 512;
  const maxBytes = options.maxBytes ?? 512 * 1024;

  const bitmap = await loadImage(file);
  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Couldn't read that image.");
  ctx.drawImage(bitmap, 0, 0, width, height);

  const transparent = hasTransparency(ctx, width, height);
  const contentType = transparent ? "image/png" : "image/jpeg";

  // PNG ignores the quality argument, so for a transparent image there's one
  // attempt and the dimension cap is what bounds the size.
  const qualities = transparent ? [undefined] : [0.9, 0.8, 0.7, 0.6];
  let best: string | null = null;
  for (const q of qualities) {
    const url = canvas.toDataURL(contentType, q);
    best = url;
    if (base64Bytes(url) <= maxBytes) break;
  }
  if (!best) throw new Error("Couldn't read that image.");

  const bytes = base64Bytes(best);
  if (bytes > maxBytes) {
    throw new Error(
      `That image is still ${Math.round(bytes / 1024)}KB after resizing. Try a simpler image.`
    );
  }

  return {
    dataBase64: best.slice(best.indexOf(",") + 1),
    contentType,
    previewUrl: best,
    bytes,
  };
}

/**
 * Decode a File into something drawable.
 *
 * createImageBitmap handles more formats and avoids a round trip through an
 * object URL, but Safari has historically been patchy — the <img> path is the
 * fallback that works everywhere.
 */
async function loadImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to the <img> path.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Couldn't read that image."));
      img.src = url;
    });
  } finally {
    // Safe immediately: the decode has finished by the time we get here.
    URL.revokeObjectURL(url);
  }
}

/**
 * True if any pixel is not fully opaque.
 *
 * Samples on a grid rather than every pixel — a logo's transparency is its
 * background, which covers a large area, so a coarse scan finds it while
 * staying cheap on a large canvas.
 */
function hasTransparency(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
  const step = Math.max(1, Math.floor(Math.min(width, height) / 64));
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      if (data[(y * width + x) * 4 + 3] < 255) return true;
    }
  }
  return false;
}

/** Decoded byte count of a data URL, without allocating the bytes. */
export function base64Bytes(dataUrl: string): number {
  const b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const padding = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}
